// =====================================================================================
// INVRT Radar - web console - campaign delivery scope (Story W4-D / W4-E)
//
// The console mirror of the HIGH-INTENT scope grammar the API validates: geography AND
// tags. Exact key sets, mode required, tags (1 to 3 distinct of A, B, C) optional on every
// mode. One definition for the single-grant coverage dialog and the bulk assign panel, so
// a draft the server would refuse never leaves the browser and a server answer outside the
// grammar fails closed.
// =====================================================================================

export type Tier = "A" | "B" | "C";
export const TIERS: readonly Tier[] = ["A", "B", "C"];

export type Scope = (
  | { mode: "all" }
  | { mode: "state"; states: string[] }
  | { mode: "city"; cities: string[] }
  | { mode: "zip"; zips: string[] }
  | { mode: "zip_range"; from: string; to: string }
) & { tags?: Tier[] };

export const STATE_CODES = new Set(
  "AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY".split(" "),
);
export const STATE_LIST: readonly string[] = [...STATE_CODES].sort();

const record = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const sameKeys = (v: Record<string, unknown>, keys: string[]) => {
  const have = Object.keys(v);
  return have.length === keys.length && keys.every((key) => Object.hasOwn(v, key));
};

function tagsValid(v: unknown): boolean {
  return Array.isArray(v) && v.length >= 1 && v.length <= 3 && new Set(v).size === v.length && v.every((t) => t === "A" || t === "B" || t === "C");
}

export function scopeValid(v: unknown): v is Scope {
  if (!record(v)) return false;
  const hasTags = Object.hasOwn(v, "tags");
  if (hasTags && !tagsValid(v.tags)) return false;
  const extra = hasTags ? ["tags"] : [];
  if (v.mode === "all") return sameKeys(v, ["mode", ...extra]);
  if (v.mode === "zip_range") {
    return sameKeys(v, ["mode", "from", "to", ...extra]) && typeof v.from === "string" && typeof v.to === "string" && /^\d{5}$/.test(v.from) && /^\d{5}$/.test(v.to) && v.from <= v.to;
  }
  const key = v.mode === "state" ? "states" : v.mode === "city" ? "cities" : v.mode === "zip" ? "zips" : null;
  if (!key || !sameKeys(v, ["mode", key, ...extra])) return false;
  const values = v[key];
  const max = key === "states" ? 51 : key === "zips" ? 500 : 100;
  return (
    Array.isArray(values) &&
    values.length > 0 &&
    values.length <= max &&
    new Set(values).size === values.length &&
    values.every((x) => {
      if (typeof x !== "string") return false;
      if (key === "states") return STATE_CODES.has(x);
      if (key === "zips") return /^\d{5}$/.test(x);
      return x === x.trim() && x.length > 0 && x.length <= 120;
    })
  );
}

const sameSet = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x) => b.includes(x));

export function sameScope(a: Scope, b: Scope): boolean {
  if (a.mode !== b.mode || !sameSet(a.tags ?? [], b.tags ?? [])) return false;
  if (a.mode === "state" && b.mode === "state") return sameSet(a.states, b.states);
  if (a.mode === "city" && b.mode === "city") return sameSet(a.cities, b.cities);
  if (a.mode === "zip" && b.mode === "zip") return sameSet(a.zips, b.zips);
  if (a.mode === "zip_range" && b.mode === "zip_range") return a.from === b.from && a.to === b.to;
  return a.mode === "all";
}

/** Splits a pasted ZIP list on commas, spaces and new lines; leading zeroes are kept (strings). */
export function parseZipText(text: string): string[] {
  return text.split(/[\s,]+/).map((part) => part.trim()).filter((part) => part.length > 0);
}

function tierText(tags: Tier[] | undefined): string | null {
  if (!tags || tags.length === 0) return null;
  const sorted = [...tags].sort();
  return sorted.length === 1 ? `tier ${sorted[0]} only` : `tiers ${sorted.slice(0, -1).join(", ")} and ${sorted[sorted.length - 1]}`;
}

export function summarise(scope: Scope): string {
  const tiers = tierText(scope.tags);
  let geography: string;
  if (scope.mode === "all") geography = "All campaign coverage";
  else if (scope.mode === "state") geography = scope.states.join(", ");
  else if (scope.mode === "city") geography = scope.cities.join(", ");
  else if (scope.mode === "zip") geography = `${scope.zips.length} ZIP ${scope.zips.length === 1 ? "code" : "codes"}`;
  else geography = `ZIP ${scope.from} to ${scope.to}`;
  return tiers ? `${geography}, ${tiers}` : geography;
}
